export type ApprovalStatus = 'IN_PROGRESS' | 'COMPLETED' | 'REJECTED' | 'CANCELLED' | string;
export type ApprovalStepType = 'VIA' | 'CONSENT' | 'FINAL' | string;
export type ApprovalStepStatus = 'WAITING' | 'PENDING' | 'APPROVED' | 'REJECTED' | string;

export const APPROVAL_TARGET_MANAGEMENT_STANDARD = 'MANAGEMENT_STANDARD';
export const APPROVAL_TARGET_INSPECTION_SHEET = 'INSPECTION_SHEET';
export const APPROVAL_TARGET_ACCEPTANCE_INSPECTION = 'ACCEPTANCE_INSPECTION';
export const APPROVAL_TARGET_MID_INSPECTION = 'MID_INSPECTION';
export const APPROVAL_TARGET_FINAL_INSPECTION = 'FINAL_INSPECTION';
export const APPROVAL_TARGET_PURCHASE_ORDER = 'PURCHASE_ORDER';
export const APPROVAL_TARGET_LEAVE = 'LEAVE';
export const APPROVAL_TARGET_WORK_REPORT = 'WORK_REPORT';
export const APPROVAL_TARGET_CLEAN_ACTIVITY_SHEET = 'CLEAN_ACTIVITY_SHEET';
export const APPROVAL_TARGET_CLEAN_ACTIVITY_EVALUATION = 'CLEAN_ACTIVITY_EVALUATION';
export const APPROVAL_TARGET_COMPLAINT_AS = 'COMPLAINT_AS';
export const APPROVAL_TARGET_NCR = 'NCR';
export const APPROVAL_TARGET_PRODUCTION_DAILY = 'PRODUCTION_DAILY';
export const APPROVAL_TARGET_INTERNAL_AUDIT_PLAN = 'INTERNAL_AUDIT_PLAN';
export const APPROVAL_TARGET_INTERNAL_AUDIT_CHECKLIST = 'INTERNAL_AUDIT_CHECKLIST';
export const APPROVAL_TARGET_INTERNAL_AUDIT_REPORT = 'INTERNAL_AUDIT_REPORT';
export const APPROVAL_TARGET_EXTERNAL_AUDIT = 'EXTERNAL_AUDIT';
export const APPROVAL_TARGET_SMALL_GROUP = 'SMALL_GROUP';
export const APPROVAL_TARGET_SMALL_GROUP_PLAN = 'SMALL_GROUP_PLAN';
export const APPROVAL_TARGET_SMALL_GROUP_RECORD = 'SMALL_GROUP_RECORD';
export const APPROVAL_TARGET_SMALL_GROUP_EVAL = 'SMALL_GROUP_EVAL';
export const APPROVAL_TARGET_QUALITY_POLICY_PLAN = 'QUALITY_POLICY_PLAN';
export const APPROVAL_TARGET_QUALITY_POLICY_DETAIL = 'QUALITY_POLICY_DETAIL';
export const APPROVAL_TARGET_QUALITY_POLICY_REPORT = 'QUALITY_POLICY_REPORT';
export const APPROVAL_TARGET_EDUCATION_ANNUAL_PLAN = 'EDUCATION_ANNUAL_PLAN';
export const APPROVAL_TARGET_EDUCATION_RECORD = 'EDUCATION_RECORD';
export const APPROVAL_TARGET_EXTERNAL_EDUCATION = 'EXTERNAL_EDUCATION';
export const APPROVAL_TARGET_PROCESS_CHART = 'PROCESS_CHART';
export const APPROVAL_TARGET_PRODUCT_MANUAL = 'PRODUCT_MANUAL';
export const APPROVAL_TARGET_DRAWING = 'DRAWING';
export const APPROVAL_TARGET_INTERNAL_DRAFT = 'INTERNAL_DRAFT';
export const APPROVAL_TARGET_MEETING_MINUTES = 'MEETING_MINUTES';
export const APPROVAL_TARGET_OFFICIAL_DOCUMENT = 'OFFICIAL_DOCUMENT';
export const APPROVAL_TARGET_IMPROVEMENT_SUGGESTION = 'IMPROVEMENT_SUGGESTION';
export const APPROVAL_TARGET_SAFETY_PPE = 'SAFETY_PPE';
export const APPROVAL_TARGET_SAFETY_CHECK = 'SAFETY_CHECK';

export interface ApprovalStep {
  stepId: number;
  stepType: ApprovalStepType;
  sortOrder: number;
  assigneeId: number;
  assigneeName?: string | null;
  status: ApprovalStepStatus;
  actedAt?: string | null;
  comment?: string | null;
}

export interface ApprovalTargetInfo {
  equipmentId?: number | null;
  equipmentCode?: string | null;
  equipmentName?: string | null;
  equipmentType?: string | null;
  sheetYear?: number | null;
  productId?: number | null;
  productName?: string | null;
  productCode?: string | null;
  inspectionId?: number | null;
  lotNo?: string | null;
  formTitle?: string | null;
  summary?: string | null;
}

export interface ApprovalRequest {
  requestId: number;
  companyId: number;
  requesterId: number;
  requesterName?: string | null;
  title: string;
  status: ApprovalStatus;
  targetType: string;
  targetId: number;
  memo?: string | null;
  createdAt?: string | null;
  completedAt?: string | null;
  currentPendingNames?: string | null;
  myPendingStepId?: number | null;
  targetInfo?: ApprovalTargetInfo | null;
  steps: ApprovalStep[];
}

export interface CreateApprovalPayload {
  title: string;
  memo?: string | null;
  targetType: string;
  targetId: number;
  viaUserIds?: number[];
  consentUserIds?: number[];
  finalUserIds: number[];
}

export function approvalStatusLabel(status?: string | null) {
  switch (status) {
    case 'IN_PROGRESS':
      return '진행중';
    case 'COMPLETED':
      return '완료';
    case 'REJECTED':
      return '반려';
    case 'CANCELLED':
      return '취소';
    default:
      return status || '-';
  }
}

export function approvalStepTypeLabel(type?: string | null) {
  switch (type) {
    case 'VIA':
      return '경유';
    case 'CONSENT':
      return '첨부';
    case 'FINAL':
      return '최종';
    default:
      return type || '-';
  }
}

export function approvalStepStatusLabel(status?: string | null) {
  switch (status) {
    case 'WAITING':
      return '대기';
    case 'PENDING':
      return '처리중';
    case 'APPROVED':
      return '승인';
    case 'REJECTED':
      return '반려';
    default:
      return status || '-';
  }
}

export function approvalTargetLabel(type?: string | null) {
  switch (type) {
    case APPROVAL_TARGET_MANAGEMENT_STANDARD:
      return '관리 기준서';
    case APPROVAL_TARGET_INSPECTION_SHEET:
      return '점검시트';
    case APPROVAL_TARGET_ACCEPTANCE_INSPECTION:
      return '인수검사성적서';
    case APPROVAL_TARGET_MID_INSPECTION:
      return '중간검사성적서';
    case APPROVAL_TARGET_FINAL_INSPECTION:
      return '제품검사성적서';
    case APPROVAL_TARGET_PURCHASE_ORDER:
      return '발주서';
    case APPROVAL_TARGET_LEAVE:
      return '휴가신청';
    case APPROVAL_TARGET_WORK_REPORT:
      return '업무보고서';
    case APPROVAL_TARGET_CLEAN_ACTIVITY_SHEET:
      return '청정활동 점검시트';
    case APPROVAL_TARGET_CLEAN_ACTIVITY_EVALUATION:
      return '청정활동 평가서';
    case APPROVAL_TARGET_COMPLAINT_AS:
      return '불만/AS';
    case APPROVAL_TARGET_NCR:
      return '부적합품 보고서';
    case APPROVAL_TARGET_PRODUCTION_DAILY:
      return '생산일보';
    case APPROVAL_TARGET_INTERNAL_AUDIT_PLAN:
      return '내부심사 계획서';
    case APPROVAL_TARGET_INTERNAL_AUDIT_CHECKLIST:
      return '내부심사 체크리스트';
    case APPROVAL_TARGET_INTERNAL_AUDIT_REPORT:
      return '내부심사 보고서';
    case APPROVAL_TARGET_EXTERNAL_AUDIT:
      return '외부심사';
    case APPROVAL_TARGET_SMALL_GROUP:
      return '소집단';
    case APPROVAL_TARGET_SMALL_GROUP_PLAN:
      return '소집단 활동계획서';
    case APPROVAL_TARGET_SMALL_GROUP_RECORD:
      return '소집단 활동기록';
    case APPROVAL_TARGET_SMALL_GROUP_EVAL:
      return '소집단 활동평가';
    case APPROVAL_TARGET_QUALITY_POLICY_PLAN:
      return '품질방침 추진계획서';
    case APPROVAL_TARGET_QUALITY_POLICY_DETAIL:
      return '품질방침 세부계획서';
    case APPROVAL_TARGET_QUALITY_POLICY_REPORT:
      return '품질방침 보고서';
    case APPROVAL_TARGET_EDUCATION_ANNUAL_PLAN:
      return '연간 교육 계획서';
    case APPROVAL_TARGET_EDUCATION_RECORD:
      return '교육 기록부';
    case APPROVAL_TARGET_EXTERNAL_EDUCATION:
      return '사외교육 수강 보고서';
    case APPROVAL_TARGET_PROCESS_CHART:
      return '공정도';
    case APPROVAL_TARGET_PRODUCT_MANUAL:
      return '사용설명서';
    case APPROVAL_TARGET_DRAWING:
      return '도면';
    case APPROVAL_TARGET_INTERNAL_DRAFT:
      return '내부 기안서';
    case APPROVAL_TARGET_MEETING_MINUTES:
      return '회의록';
    case APPROVAL_TARGET_OFFICIAL_DOCUMENT:
      return '공문서';
    case APPROVAL_TARGET_IMPROVEMENT_SUGGESTION:
      return '제안 신청서';
    case APPROVAL_TARGET_SAFETY_PPE:
      return '안전장비 지급현황';
    case APPROVAL_TARGET_SAFETY_CHECK:
      return '안전관리 점검시트';
    default:
      return type || '-';
  }
}
